import { Box, Button, Chip, Container, Paper, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { usePageView } from '../../utils/analytics';
import { BUILD_PROFILE } from '../../utils/buildProfile';
import { trackEvent } from '../../utils/tracking';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  usePageView('About');

  const brandDomainByBuild = {
    presentear: 'presentear.net',
    chadebebe: 'chadebebe.net',
    listacasamento: 'listacasamento.net',
    chadebb: 'chadebb.com',
  } as const;

  const siteDomain = brandDomainByBuild[BUILD_PROFILE.brand];
  const contactEmail = `contato@${siteDomain}`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    trackEvent('contact_email_about', {
      action: 'click',
      element: 'contact_email_button',
    });
    window.location.href = `mailto:${contactEmail}`;
  };

  return (
    <Box className="about-page">
      {/* Hero Section */}
      <Box className="about-hero" sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            sx={{
              mb: 2,
              fontWeight: 'bold',
              color: '#2c3e50',
              textAlign: 'center',
            }}
          >
            Sobre Nós
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#7f8c8d',
              textAlign: 'center',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {siteDomain} é uma plataforma inovadora de listas de presentes, criada para simplificar
            o jeito de presentear.
          </Typography>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Mission & Values */}
        <Box sx={{ display: 'flex', gap: 4, mb: 8, flexDirection: { xs: 'column', md: 'row' } }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: '#ecf0f1',
              borderLeft: '4px solid #e74c3c',
              flex: 1,
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
              Nossa Missão
            </Typography>
            <Typography sx={{ color: '#555', lineHeight: 1.8 }}>
              Oferecer uma solução simples, segura e elegante para criar listas de presentes
              personalizadas. Desde casamentos até chás de bebê, queremos que você foque no que
              realmente importa: celebrar momentos especiais com as pessoas que ama.
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 4,
              backgroundColor: '#fdf2e9',
              borderLeft: '4px solid #f39c12',
              flex: 1,
            }}
          >
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
              Valores
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label="Segurança" color="primary" />
              <Chip label="Simplicidade" color="primary" />
              <Chip label="Inovação" color="primary" />
            </Box>
            <Typography sx={{ color: '#555', lineHeight: 1.8 }}>
              Trabalhamos com os mais altos padrões de segurança de dados, interface intuitiva e
              sempre buscando inovações para melhorar sua experiência.
            </Typography>
          </Paper>
        </Box>

        {/* Founder */}
        <Paper
          elevation={0}
          sx={{
            p: 6,
            backgroundColor: '#ecf0f1',
            borderRadius: 2,
            textAlign: 'center',
            mb: 8,
          }}
        >
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#2c3e50' }}>
            Desenvolvedor & Fundador
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, color: '#e74c3c', fontWeight: 600 }}>
            Cristian Terence dos Santos
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 3,
              justifyContent: 'center',
              mt: 2,
              mb: 3,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ p: 2, minWidth: '200px' }}>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem', mb: 1 }}>
                Profissão
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>
                Desenvolvedor Independente
              </Typography>
            </Box>
            <Box sx={{ p: 2, minWidth: '200px' }}>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem', mb: 1 }}>
                Especialidade
              </Typography>
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>
                Full Stack (React, Node.js, PostgreSQL)
              </Typography>
            </Box>
            <Box sx={{ p: 2, minWidth: '200px' }}>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem', mb: 1 }}>Foco</Typography>
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', fontSize: '1.1rem' }}>
                Plataformas SaaS
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ color: '#555', lineHeight: 1.8, mb: 3 }}>
            Com mais de 8 anos de experiência em desenvolvimento web, criei {siteDomain} para
            resolver um problema real: falta de plataformas simples e seguras para gerenciar listas
            de presentes no Brasil.
          </Typography>

          <Button
            variant="contained"
            sx={{
              backgroundColor: '#e74c3c',
              color: '#fff',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#c0392b',
              },
            }}
            onClick={handleContactClick}
          >
            📧 Envie-me uma mensagem
          </Button>
        </Paper>

        {/* Technology */}
        <Paper elevation={0} sx={{ p: 4, mb: 8 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50' }}>
            Tecnologia
          </Typography>
          <Typography sx={{ color: '#555', lineHeight: 1.8, mb: 2 }}>
            {siteDomain} foi construída com as tecnologias mais modernas e confiáveis:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{ p: 2, backgroundColor: '#ecf0f1', borderRadius: 1, flex: 1, minWidth: '250px' }}
            >
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>Frontend</Typography>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                React 19, TypeScript, Material-UI, Vite
              </Typography>
            </Box>
            <Box
              sx={{ p: 2, backgroundColor: '#ecf0f1', borderRadius: 1, flex: 1, minWidth: '250px' }}
            >
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>Backend</Typography>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                Node.js, Express, PostgreSQL, Prisma
              </Typography>
            </Box>
            <Box
              sx={{ p: 2, backgroundColor: '#ecf0f1', borderRadius: 1, flex: 1, minWidth: '250px' }}
            >
              <Typography sx={{ fontWeight: 600, color: '#2c3e50', mb: 1 }}>
                Infraestrutura
              </Typography>
              <Typography sx={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                Vercel, Railway, AWS, Cloudflare CDN
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Security & Privacy */}
        <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
          <Paper elevation={0} sx={{ p: 4, backgroundColor: '#ecf0f1', flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
              🔒 Segurança
            </Typography>
            <Typography sx={{ color: '#555', lineHeight: 1.8 }}>
              Todos os dados são criptografados em trânsito (HTTPS) e protegidos contra ataques
              comuns (XSS, CSRF, SQL Injection). Seus dados jamais são vendidos ou compartilhados.
            </Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 4, backgroundColor: '#fdf2e9', flex: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
              📋 Privacidade
            </Typography>
            <Typography sx={{ color: '#555', lineHeight: 1.8 }}>
              Somos 100% em conformidade com a LGPD (Lei Geral de Proteção de Dados). Você controla
              seus dados e pode solicitar exclusão a qualquer momento.
            </Typography>
          </Paper>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8, mt: 8, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#2c3e50' }}>
            Tem alguma dúvida?
          </Typography>
          <Typography sx={{ color: '#7f8c8d', mb: 3 }}>
            Entre em contato direto comigo. Adorarei ouvir seu feedback!
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#e74c3c',
              color: '#fff',
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#c0392b',
              },
            }}
            onClick={handleContactClick}
          >
            📧 {contactEmail}
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
